//React
import React, { useCallback, useRef, useEffect, useLayoutEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

//APP
import Routes from "../../../routes/Routes";
import { DefaultEditorSwimlane } from "../../swimLane";
import { DiagramList, InsufficientCreditsModal } from "../../../views/modal";
import { FamlaServices } from "../../../services";
import type { FamlaServicesResponse } from "../../../services/api/FamlaServices";
import { simlane_export } from "../../swimLane/static/data";
import { SwimlaneError } from "../../../components";
import MappingGenerationPlaceholder from "../../../components/MappingGenerationPlaceholder";
import { mapId, useAppDispatch, useAppSelector } from "../../../stores";
import { checkMappingOpenAccess, type LocalSheetRef, type MappingNavigationState } from "../../../utils/mappingAccess";
import {
  hideMappingLoadingOverlay,
  isInterviewCalculateDone,
  isInterviewDeferredMappingOpen,
  markInterviewCalculateDone,
  clearInterviewDeferredMappingOpen,
} from "../../../utils/mappingLoadingOverlay";
import { useInterviewMappingReveal } from "../../../hooks/useInterviewMappingReveal";
import OutlookPromoModal from "../../../components/OutlookPromoModal";
import '../../../../src/assets/css/diagram.css'
import '../../../styles/interview-redesign.css'
import { LayerSelections } from "../../../swimlane/lib/types";
import {
  canUseLocalSheetCache,
  diagramId,
  hasCompleteLocalSheet,
  mergeSheetDetail,
  mergeProcessListWithLocalCache,
  resolveSheetOnUrlChange,
  sheetNeedsPainPointsEnrich,
  shouldReloadProcessListOnSwitch,
  shouldSkipApprovalForNavigation,
  type MapSwitchGuards,
} from "../../../swimlane/lib/utils/sheetCacheUtils";
import { MAP_SWITCH_FAST_PATH, MAP_BATCH_PREFETCH } from "../../../swimlane/lib/utils/mapSwitchFlags";
import { dedupeSheetFetch } from "../../../swimlane/lib/utils/sheetFetchDedup";
import { warmParsedSheetCache, invalidateParsedSheetCache } from "../../../swimlane/lib/utils/parsedSheetCache";
import {
  beginMapSwitch,
  endMapSwitch,
  markMapSwitchPhase,
} from "../../../swimlane/lib/utils/mapSwitchPerf";

function resolvePrefetchedOpenBootstrap(
  navState: MappingNavigationState | null,
  urlMapId: string | undefined,
): { sheet: LocalSheetRef; sheets: LocalSheetRef[] } | null {
  if (!urlMapId) return null;
  if (navState?.skipPrefetchedInstantOpen || isInterviewDeferredMappingOpen(urlMapId)) {
    return null;
  }
  const sheets = navState?.prefetchedSheets;
  if (!sheets?.length || !hasCompleteLocalSheet(sheets, urlMapId)) return null;
  const sheet = sheets.find((item) => diagramId(item) === String(urlMapId));
  if (!sheet) return null;
  return { sheet, sheets };
}

const ProcessDiagram = () => {
  const { id, sId, AnalyseId } = useParams();
  const location = useLocation();
  const mappingNavState = (location.state as MappingNavigationState | null) ?? null;
  const interviewNavDeferred =
    mappingNavState?.skipPrefetchedInstantOpen === true ||
    isInterviewDeferredMappingOpen(sId);
  const [interviewRevealComplete, setInterviewRevealComplete] = React.useState(false);
  const interviewDeferredOpen = interviewNavDeferred && !interviewRevealComplete;
  const prefetchedOpenBootstrap = resolvePrefetchedOpenBootstrap(mappingNavState, sId);
  const knownAccessibleFromNav = mappingNavState?.knownAccessible === true;
  const { t } = useTranslation();
  const [swimLane, setSwimlane] = React.useState<any>(prefetchedOpenBootstrap?.sheet ?? simlane_export);
  const swimLaneRef = useRef<any[]>(prefetchedOpenBootstrap ? [...prefetchedOpenBootstrap.sheets] : []);
  const swimLaneStateRef = useRef(swimLane);
  swimLaneStateRef.current = swimLane;
  const [allSwimLane, setAllSwimLane] = React.useState<any[]>([]);
  const [approvallist, setApprovallist] = React.useState([]);
  const users = useAppSelector((state) => state.auth);
  const company = useAppSelector((state) => state.company.company);
  const role = useAppSelector((state) => state.role.role);
  /** Id feuille pour l’éditeur : payload courant (à jour après setMap), sinon segment d’URL. */
  const liveMapId =
    ((swimLane as any)?._id ?? (swimLane as any)?.id ?? sId ?? "") as string;
  const liveMapIdRef = useRef(liveMapId);
  liveMapIdRef.current = liveMapId;
  
  // Déterminer si l'utilisateur actuel est ADMIN (seuls les ADMIN peuvent choisir un plan)
  const isAdmin = role === "ADMIN" || role === "admin";
  // Seuls les ADMIN peuvent voir le bouton "Choisir plan"
  const isOwner = isAdmin;
  // Récupérer l'email du propriétaire de la company pour les utilisateurs non-admin
  const [ownerEmail, setOwnerEmail] = React.useState<string | undefined>(undefined);
  
  // Récupérer l'email du propriétaire via l'API
  useEffect(() => {
    const loadOwnerEmail = async () => {
      if (isAdmin || !users.session?.company_id || !users.access_token) return;
      
      try {
        const data = await FamlaServices("api_host", `/company/by/id/${users.session.company_id}`, "GET", "", users.access_token);
        
        if (data.status === 200 && data.body?.data?.owner?.email) {
          setOwnerEmail(data.body.data.owner.email);
        }
      } catch (error) {
        // Erreur silencieuse
      }
    };
    
    loadOwnerEmail();
  }, [isAdmin, users.session?.company_id, users.access_token]);
  
  const [bootSpinner, setBootSpinner] = React.useState(
    !interviewDeferredOpen && !prefetchedOpenBootstrap,
  );
  const [time, setTime] = React.useState(0);
  const [layer, setLayer] = React.useState<LayerSelections>({
    block: [],
    line: [],
    startEnd: []
  });
  const [processesWithDiagrams, setProcessesWithDiagrams] = React.useState<any[]>([]);
  const [canEditMap, setCanEditMap] = React.useState(false);
  /** Porteur + co-propriétaire (API /process/my/role) — menu Propriétaire en cartographie */
  const [isProcessMapManager, setIsProcessMapManager] = React.useState(false);
  const [collaborators, setcollaborators] = React.useState([]);
  const closeRef = useRef<HTMLButtonElement>(null);
  const [isMapGenerationFailed, setIsMapGenerationFailed] = React.useState(false);
  const [mapGenPending, setMapGenPending] = React.useState(false);
  const [mapAccessBlocked, setMapAccessBlocked] = React.useState(false);
  const [isMapSwitching, setIsMapSwitching] = React.useState(false);
  const [mapSwitchTargetId, setMapSwitchTargetId] = React.useState<string | null>(null);
  const mapSwitchTargetIdRef = useRef<string | null>(null);
  mapSwitchTargetIdRef.current = mapSwitchTargetId;
  const switchGuardsRef = useRef<MapSwitchGuards>({
    hasUnsavedChanges: false,
    saveStatus: 'idle',
    mapGenPending: false,
    forceSlowPath: false,
  });
  const backgroundRefreshTokenRef = useRef(0);
  const sheetEtagByMapIdRef = useRef<Record<string, string>>({});
  /** Compteur monotone pour détecter les appels setMap périmés (race condition). */
  const setMapCallCountRef = useRef(0);
  /** Dernier sId "voulu" (toujours à jour de façon synchrone) — garde anti-course pour fetchMapPayload. */
  const latestSIdRef = useRef(sId);
  latestSIdRef.current = sId;
  const mapSwitchClearTimerRef = useRef<number | null>(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const insufficientCreditsModalRef = useRef<HTMLSpanElement>(null);
  const [insufficientCreditsInfo, setInsufficientCreditsInfo] = React.useState<{
    creditsRequired: number;
    creditsAvailable: number;
    deficit: number;
    plan?: string;
  } | null>(null);

  const prefetchedSheetsAppliedRef = useRef(false);
  const interviewDeferredOpenRef = useRef(interviewDeferredOpen);
  interviewDeferredOpenRef.current = interviewDeferredOpen;

  useLayoutEffect(() => {
    if (mappingNavState?.skipPrefetchedInstantOpen !== true) {
      clearInterviewDeferredMappingOpen();
    }
  }, [mappingNavState?.skipPrefetchedInstantOpen]);

  const editorBlocked = mapGenPending || mapAccessBlocked || isMapGenerationFailed;

  const interviewReveal = useInterviewMappingReveal({
    interviewDeferredOpen,
    sId,
    time,
    editorBlocked,
    onRevealComplete: () => setInterviewRevealComplete(true),
  });

  const finishOpenDisplay = useCallback(() => {
    if (interviewDeferredOpenRef.current) {
      interviewReveal.finishOpenDisplay();
      return;
    }
    setBootSpinner(false);
    hideMappingLoadingOverlay();
  }, [interviewReveal]);

  /** Évite un second GET après setMap + navigate quand le payload local est déjà complet. */
  const suppressFetchForMapIdRef = useRef<string | null>(
    prefetchedOpenBootstrap && sId ? String(sId) : null,
  );
  /** setMap a déjà planifié approval/edit + approval/{id} — l'effet sId ne doit pas dupliquer. */
  const skipApprovalSideEffectsRef = useRef<string | null>(
    prefetchedOpenBootstrap && sId ? String(sId) : null,
  );
  /** Une seule tentative d'enrich pain points (calculate/new) par map et par session. */
  const painEnrichTriedRef = useRef<Set<string>>(new Set());

  useLayoutEffect(() => {
    if (prefetchedSheetsAppliedRef.current) return;
    const sheets = mappingNavState?.prefetchedSheets;
    if (!sheets?.length) return;
    prefetchedSheetsAppliedRef.current = true;
    swimLaneRef.current = [...sheets];
    setAllSwimLane([...sheets]);

    if (
      sId &&
      hasCompleteLocalSheet(sheets, sId) &&
      !mappingNavState?.skipPrefetchedInstantOpen &&
      !isInterviewDeferredMappingOpen(sId)
    ) {
      const localSheet = sheets.find((item) => diagramId(item) === String(sId));
      if (localSheet?.swimlane && localSheet?.line) {
        setSwimlane(localSheet);
        dispatch(mapId({ mapId: String(sId) }));
        warmParsedSheetCache(swimLaneRef.current);
        suppressFetchForMapIdRef.current = String(sId);
        skipApprovalSideEffectsRef.current = String(sId);
        finishOpenDisplay();
        return;
      }
    }

    warmParsedSheetCache(swimLaneRef.current);
  }, [
    mappingNavState?.prefetchedSheets,
    mappingNavState?.skipPrefetchedInstantOpen,
    sId,
    dispatch,
    finishOpenDisplay,
  ]);

  // Ouvrir le modal quand insufficientCreditsInfo est défini
  useEffect(() => {
    if (insufficientCreditsInfo && insufficientCreditsModalRef.current) {
      // Attendre que le modal soit monté dans le DOM
      setTimeout(() => {
        if (insufficientCreditsModalRef.current) {
          insufficientCreditsModalRef.current.click();
        }
      }, 100);
    }
  }, [insufficientCreditsInfo]);

  const clearMapSwitchingOverlay = useCallback(() => {
    if (mapSwitchClearTimerRef.current != null) {
      window.clearTimeout(mapSwitchClearTimerRef.current);
      mapSwitchClearTimerRef.current = null;
    }
    setMapSwitchTargetId(null);
    setIsMapSwitching(false);
  }, []);

  const scheduleMapSwitchingFallbackClear = useCallback(() => {
    if (mapSwitchClearTimerRef.current != null) {
      window.clearTimeout(mapSwitchClearTimerRef.current);
    }
    mapSwitchClearTimerRef.current = window.setTimeout(() => {
      setMapSwitchTargetId(null);
      setIsMapSwitching(false);
      mapSwitchClearTimerRef.current = null;
    }, 12_000);
  }, []);

  const fetchSheetDetail = useCallback(
    async (
      mapId: string,
      endpoint: 'calculate' | 'sheet' = 'calculate',
      options?: { ifNoneMatch?: boolean; includePreview?: boolean }
    ) => {
      const path =
        endpoint === 'sheet'
          ? `/mapping/sheet/${mapId}${options?.includePreview ? '?include_preview=true' : ''}`
          : `/mapping/calculate/new/${mapId}?include_preview=${options?.includePreview !== false ? 'true' : 'false'}`;
      const dedupeEndpoint =
        endpoint === 'sheet' && options?.includePreview ? 'sheet-preview' : endpoint;
      const extraHeaders: Record<string, string> | null =
        endpoint === 'sheet' &&
        options?.ifNoneMatch &&
        sheetEtagByMapIdRef.current[mapId]
          ? { 'If-None-Match': sheetEtagByMapIdRef.current[mapId] }
          : null;
      const result = await dedupeSheetFetch<FamlaServicesResponse<{ data?: Record<string, unknown>; status_code?: number }>>(
        mapId,
        dedupeEndpoint,
        () =>
          FamlaServices("api_host", path, "GET", "", users.access_token, extraHeaders)
      );
      if (endpoint === 'sheet' && result.status === 200) {
        const headers = result.headers ?? {};
        const etag = headers.etag || headers.ETag;
        if (etag) {
          sheetEtagByMapIdRef.current[mapId] = etag;
        }
      }
      return result;
    },
    [users.access_token]
  );

  /** GET calculate/new si la feuille n'a pas encore de post-its pain_points. */
  const fetchPainPointsEnrichIfNeeded = useCallback(
    async (
      mapId: string,
      base: Record<string, unknown>
    ): Promise<Record<string, unknown>> => {
      if (!sheetNeedsPainPointsEnrich(base)) return base;
      if (painEnrichTriedRef.current.has(mapId)) return base;
      try {
        const detail = await fetchSheetDetail(mapId, 'calculate', { includePreview: true });
        if (detail.status === 200 && detail.body?.data) {
          painEnrichTriedRef.current.add(mapId);
          const merged = mergeSheetDetail(base, detail.body.data as Record<string, unknown>);
          invalidateParsedSheetCache(mapId);
          return merged;
        }
      } catch {
        /* garde le payload sans notes — retente possible au prochain chargement */
      }
      return base;
    },
    [fetchSheetDetail]
  );

  const applyEditorPayload = useCallback(
    (payload: Record<string, unknown> | undefined, requestedSId: string) => {
      if (!payload) return;
      invalidateParsedSheetCache(requestedSId);
      setSwimlane(payload);
      const payloadId = diagramId(payload as { _id?: string; id?: string });
      if (payloadId) {
        dispatch(mapId({ mapId: payloadId }));
        if (swimLaneRef.current.length === 0) {
          swimLaneRef.current = [{ ...payload }];
        } else {
          swimLaneRef.current = swimLaneRef.current.map((item) =>
            diagramId(item) === payloadId ? { ...item, ...payload } : item,
          );
        }
        setAllSwimLane([...swimLaneRef.current]);
      }
      const generating = payload.is_finish === false;
      if (generating) {
        console.warn('[mapGenPending] activé via fetchMapPayload pour', requestedSId);
      }
      setMapGenPending(generating);
    },
    [dispatch],
  );

  const applyLocalNavigation = useCallback(
    (_id: string, merged: any) => {
      setSwimlane(merged);
      const mergedId = diagramId(merged);
      if (mergedId) {
        swimLaneRef.current = swimLaneRef.current.map((item: any) =>
          diagramId(item) === mergedId ? { ...item, ...merged } : item
        );
        setAllSwimLane([...swimLaneRef.current]);
      }
      const stillGenerating = merged?.is_finish === false;
      if (stillGenerating) {
        console.warn('[mapGenPending] activé via applyLocalNavigation pour', _id);
      }
      setMapGenPending(stillGenerating);
      if (!stillGenerating) {
        setIsMapGenerationFailed(false);
      }
      dispatch(mapId({ mapId: _id }));
      skipApprovalSideEffectsRef.current = _id;
      if (id && _id && sId !== _id) {
        suppressFetchForMapIdRef.current = _id;
        const path = AnalyseId
          ? `${Routes.MAPPING}${id}/${_id}/${AnalyseId}`
          : `${Routes.MAPPING}${id}/${_id}`;
        navigate(path, { replace: true });
      }
      closeRef.current?.click();
    },
    [AnalyseId, dispatch, id, navigate, sId]
  );

  const scheduleBackgroundSheetRefresh = useCallback(
    (targetId: string, localUpdatedAt?: string) => {
      if (!MAP_SWITCH_FAST_PATH) return;
      const localNow = swimLaneRef.current.find((d) => diagramId(d) === targetId) as
        | Record<string, unknown>
        | undefined;
      const needsPainEnrich = sheetNeedsPainPointsEnrich(localNow);
      if (hasCompleteLocalSheet(swimLaneRef.current, targetId) && !needsPainEnrich) {
        return;
      }

      const token = ++backgroundRefreshTokenRef.current;
      const run = () => {
        void (async () => {
          if (backgroundRefreshTokenRef.current !== token) return;
          try {
            markMapSwitchPhase('background_refresh');
            let server: Record<string, unknown> | null = null;
            if (needsPainEnrich) {
              const detail = await fetchSheetDetail(targetId, 'calculate');
              if (detail.status === 200 && detail.body?.data) {
                server = detail.body.data as Record<string, unknown>;
                painEnrichTriedRef.current.add(targetId);
              }
            } else {
              let detail = await fetchSheetDetail(targetId, 'sheet', { ifNoneMatch: true });
              if (detail.status === 304) return;
              if (detail.status !== 200) {
                detail = await fetchSheetDetail(targetId, 'calculate');
              }
              if (detail.status === 200 && detail.body?.data) {
                server = detail.body.data as Record<string, unknown>;
              }
            }
            if (!server) return;
            const serverPayload: Record<string, unknown> = server;
            const serverUpdated = String(serverPayload.updated_at ?? '');
            if (
              !needsPainEnrich &&
              localUpdatedAt &&
              serverUpdated &&
              serverUpdated <= localUpdatedAt
            ) {
              return;
            }

            const local = swimLaneRef.current.find((d) => diagramId(d) === targetId);
            if (!local) return;
            const merged = mergeSheetDetail(local, serverPayload);
            swimLaneRef.current = swimLaneRef.current.map((item) =>
              diagramId(item) === targetId ? { ...item, ...merged } : item
            );
            setAllSwimLane([...swimLaneRef.current]);
            if (liveMapIdRef.current === targetId) {
              setSwimlane((prev: any) => mergeSheetDetail(prev ?? {}, serverPayload));
            }
          } catch {
            /* best-effort */
          }
        })();
      };

      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(() => window.setTimeout(run, 3000));
      } else {
        window.setTimeout(run, 3500);
      }
    },
    [fetchSheetDetail]
  );

  const prefetchAdjacentSheets = useCallback(
    async (sheets: any[], activeId: string) => {
      if (!MAP_BATCH_PREFETCH || !users.access_token) return;
      const ids = sheets.map((s) => diagramId(s)).filter(Boolean) as string[];
      if (ids.length < 2) return;
      const idx = ids.indexOf(activeId);
      const neighbors = [
        idx > 0 ? ids[idx - 1] : null,
        idx >= 0 && idx < ids.length - 1 ? ids[idx + 1] : null,
      ].filter(Boolean) as string[];
      if (neighbors.length === 0) return;
      try {
        const qs = neighbors.map((mid) => `ids=${encodeURIComponent(mid)}`).join('&');
        await FamlaServices(
          'api_host',
          `/mapping/sheets/batch?${qs}`,
          'GET',
          '',
          users.access_token
        );
      } catch {
        /* best-effort */
      }
    },
    [users.access_token]
  );

  const loadAllSwimlane = useCallback(async (): Promise<any[]> => {
    try {
      const data = await FamlaServices(
        "api_host",
        `/mapping/process/${id}?fields=editor&skip_enrich=true`,
        "GET",
        "",
        users.access_token
      );
      
      // [CAS 1] Status 200 - Tous les mappings sont accessibles
      if (data.status === 200) {
        const responseData = data.body.data;
        
        // Gérer le format (peut être array direct ou objet avec accessible)
        let newData = Array.isArray(responseData) ? responseData : responseData.accessible || [];

        // If swimLaneRef is empty, initialize it with the first data
        if (swimLaneRef.current.length === 0) {
          swimLaneRef.current = [...newData];
          setAllSwimLane([...newData]);
          warmParsedSheetCache(swimLaneRef.current);
          void prefetchAdjacentSheets(newData, liveMapIdRef.current || diagramId(newData[0]) || '');
          return [...swimLaneRef.current];
        }

        // Create a new array maintaining the original order and updating existing items
        swimLaneRef.current = mergeProcessListWithLocalCache(swimLaneRef.current, newData);
        setAllSwimLane([...swimLaneRef.current]);
        warmParsedSheetCache(swimLaneRef.current);
        return [...swimLaneRef.current];
      }
      
      // [CAS 2] Status 402 - Crédits insuffisants (pay-to-view)
      // Nouveau format: locked est un objet unique (processus bloqué), pas un array
      else if (data.status === 402) {
        const response = data.body.data;
        
        // Afficher les mappings accessibles (anciennes cartographies)
        if (response.accessible && response.accessible.length > 0) {
          swimLaneRef.current = [...response.accessible];
          setAllSwimLane([...response.accessible]);
        }
        
        // Gérer le processus bloqué (facturation basée sur max des versions)
        if (response.locked) {
          // Stocker les informations pour le modal (le useEffect s'occupera de l'ouvrir)
          setInsufficientCreditsInfo({
            creditsRequired: response.locked.credits_required,
            creditsAvailable: response.locked.credits_available,
            deficit: response.locked.deficit,
            plan: response.locked.plan || "FREE"
          });
          
          // Afficher aussi les mappings bloqués dans la liste
          if (response.locked.mappings && response.locked.mappings.length > 0) {
            swimLaneRef.current = [...swimLaneRef.current, ...response.locked.mappings];
            setAllSwimLane([...swimLaneRef.current]);
          }
        }
        return [...swimLaneRef.current];
      }
    } catch (error) {
      console.error("Error loading swimlanes:", error);
    }
    return [...swimLaneRef.current];
  }, [id, prefetchAdjacentSheets, users.access_token]);

  const loadProcessesWithDiagrams = async () => {
    try {
      // Récupérer tous les processus
      const processResponse = await FamlaServices("api_host", "/process/my", "GET", "", users.access_token);

      if (processResponse.status === 200) {
        const processes = processResponse.body.data;

        // Récupérer les diagrammes pour chaque processus
        const processesWithDiagrams = await Promise.all(
          processes.map(async (process: any) => {
            try {
              const diagramResponse = await FamlaServices("api_host", `/mapping/process/${process._id}`, "GET", "", users.access_token);
              
              // Gérer status 200 (accessibles) et 402 (certains bloqués)
              if (diagramResponse.status === 200) {
                const responseData = diagramResponse.body.data;
                // Format peut être array direct ou objet avec accessible
                const diagrams = Array.isArray(responseData) ? responseData : responseData.accessible || [];
                return {
                  ...process,
                  diagrams: diagrams,
                };
              } else if (diagramResponse.status === 402) {
                // Retourner seulement les mappings accessibles
                const accessible = diagramResponse.body.data.accessible || [];
                return {
                  ...process,
                  diagrams: accessible,
                };
              } else {
                return { ...process, diagrams: [] };
              }
            } catch (error) {
              console.error(`Erreur lors du chargement des diagrammes pour le processus ${process._id}:`, error);
              return { ...process, diagrams: [] };
            }
          })
        );

        // Retourner ou stocker le résultat
        return processesWithDiagrams;
      } else {
        console.error("Erreur lors du chargement des processus.");
        return [];
      }
    } catch (error) {
      console.error("Erreur générale dans loadProcessesWithDiagrams:", error);
      return [];
    }
  };

  const loadLayerByprocessId = useCallback(async () => {
    try {
      const data = await FamlaServices("api_host", `/process/by/id/${id}`, "GET", "", users.access_token);
      if (data.status === 200) {
        const DataLayer = data.body.data;
        setLayer({
          block: DataLayer.block_layers,
          line: DataLayer.line_layers,
          startEnd: DataLayer.start_end_layers
        });
      }
    } catch (error) {
    }
  }, [id, users.access_token]);

  const loadProcessMapRole = React.useCallback(async () => {
    if (!id) {
      setIsProcessMapManager(false);
      return;
    }
    try {
      const res = await FamlaServices(
        "api_host",
        `/process/my/role/${id}`,
        "GET",
        "",
        users.access_token
      );
      const ok = res.status === 200 && res.body?.data?.is_process_manager === true;
      setIsProcessMapManager(!!ok);
    } catch {
      setIsProcessMapManager(false);
    }
  }, [id, users.access_token]);

  React.useEffect(() => {
    if (!sId) return;

    if (shouldSkipApprovalForNavigation(skipApprovalSideEffectsRef.current, sId)) {
      skipApprovalSideEffectsRef.current = null;
      dispatch(mapId({ mapId: sId }));
      return;
    }

    checkMapEditPermission(sId);
    dispatch(mapId({ mapId: sId }));
    loadCollaborators(sId);
  }, [sId, dispatch]);

  React.useEffect(() => {
    const loadBase = async () => {
      const hasPrefetchedSheets = Boolean(mappingNavState?.prefetchedSheets?.length);
      await Promise.all([
        loadLayerByprocessId(),
        hasPrefetchedSheets ? Promise.resolve() : loadAllSwimlane(),
        loadApprovalList(),
        loadTime(),
        loadProcessMapRole()
      ]);
    };

    void loadBase();
  }, [id, users.access_token, loadLayerByprocessId, loadAllSwimlane, loadProcessMapRole, mappingNavState?.prefetchedSheets]);

  React.useEffect(() => {
    if (!sId || !swimLaneRef.current.length) return;
    void prefetchAdjacentSheets(swimLaneRef.current, sId);
  }, [sId, prefetchAdjacentSheets]);

  React.useEffect(() => {
    const fetchData = async () => {
      const data = await loadProcessesWithDiagrams();
      setProcessesWithDiagrams(data);
    };

    fetchData();
  }, [id]);


  const fetchMapPayload = React.useCallback(async (options?: {
    fullEditorPayload?: boolean;
    /** Réouverture interview — sheet + preview, sans calculate/new. */
    interviewReopen?: boolean;
  }) => {
    if (!sId) return undefined;
    const requestedSId = sId;
    try {
      let data;
      if (options?.fullEditorPayload) {
        data = await fetchSheetDetail(requestedSId, 'calculate', { includePreview: true });
      } else if (options?.interviewReopen) {
        data = await fetchSheetDetail(requestedSId, 'sheet', { includePreview: true });
      } else {
        data = await fetchSheetDetail(requestedSId, 'sheet');
        if (latestSIdRef.current !== requestedSId) return undefined;
        if (data.status !== 200) {
          data = await fetchSheetDetail(requestedSId, 'calculate', { includePreview: true });
        }
      }
      if (latestSIdRef.current !== requestedSId) return undefined;
      if (data.status === 200) {
        let payload = data.body?.data as Record<string, unknown> | undefined;
        if (payload && !options?.fullEditorPayload && !options?.interviewReopen) {
          payload = await fetchPainPointsEnrichIfNeeded(requestedSId, payload);
          if (latestSIdRef.current !== requestedSId) return undefined;
        }
        applyEditorPayload(payload, requestedSId);
        setIsMapGenerationFailed(false);
        return payload;
      }
      if (data.body?.status_code === 400) {
        const localFallback = swimLaneRef.current.find((d) => diagramId(d) === requestedSId);
        if (localFallback && hasCompleteLocalSheet(swimLaneRef.current, requestedSId)) {
          applyEditorPayload(localFallback as Record<string, unknown>, requestedSId);
          setIsMapGenerationFailed(false);
          return localFallback as Record<string, unknown>;
        }
        setIsMapGenerationFailed(true);
        setMapGenPending(false);
      }
    } catch (error) {
      console.error("Error loading new swimlane:", error);
    }
    return undefined;
  }, [sId, fetchSheetDetail, fetchPainPointsEnrichIfNeeded, applyEditorPayload]);

  const loadNewSwimlane = React.useCallback(async () => {
    if (!sId || !id) {
      finishOpenDisplay();
      return;
    }

    setMapAccessBlocked(false);
    if (!knownAccessibleFromNav) {
      const access = await checkMappingOpenAccess(
        id,
        sId,
        users.access_token || '',
      );
      if (!access.allowed) {
        setInsufficientCreditsInfo(access.denied);
        setMapAccessBlocked(true);
        finishOpenDisplay();
        return;
      }
    }

    const fromInterview = interviewDeferredOpenRef.current;
    const firstInterviewOpen = fromInterview && !isInterviewCalculateDone(sId);
    let payload: Record<string, unknown> | undefined;

    if (firstInterviewOpen) {
      payload = await fetchMapPayload({ fullEditorPayload: true });
      if (payload && sheetNeedsPainPointsEnrich(payload)) {
        payload = await fetchPainPointsEnrichIfNeeded(sId, payload);
        if (latestSIdRef.current === sId) {
          applyEditorPayload(payload, sId);
        }
      }
      if (payload) {
        markInterviewCalculateDone(sId);
      }
    } else if (fromInterview) {
      payload = await fetchMapPayload({ interviewReopen: true });
    } else {
      payload = await fetchMapPayload();
    }

    if (fromInterview && !payload) {
      const localFallback = swimLaneRef.current.find((d) => diagramId(d) === sId);
      if (localFallback && hasCompleteLocalSheet(swimLaneRef.current, sId)) {
        applyEditorPayload(localFallback as Record<string, unknown>, sId);
      } else {
        setIsMapGenerationFailed(true);
      }
    } else if (payload) {
      setIsMapGenerationFailed(false);
    }

    finishOpenDisplay();
  }, [sId, id, users.access_token, fetchMapPayload, fetchPainPointsEnrichIfNeeded, applyEditorPayload, knownAccessibleFromNav, finishOpenDisplay]);

  React.useEffect(() => {
    if (!sId) return;

    if (interviewDeferredOpenRef.current) {
      void loadNewSwimlane();
      return;
    }

    const current = swimLaneStateRef.current as {
      _id?: string;
      id?: string;
      swimlane?: string;
      line?: string;
    };
    const currentSheetId = diagramId(current);
    const currentHasPayload = Boolean(current?.swimlane && current?.line);
    const suppressId = suppressFetchForMapIdRef.current;
    const pendingSheetId = suppressId || mapSwitchTargetIdRef.current || null;

    const resolution = resolveSheetOnUrlChange({
      urlSheetId: sId,
      currentSheetId,
      pendingSheetId,
      suppressFetchForSheetId: suppressId,
      localSheets: swimLaneRef.current,
      currentHasPayload,
    });

    if (suppressId === sId) {
      suppressFetchForMapIdRef.current = null;
    }

    switch (resolution.action) {
      case 'skip':
      case 'keepCurrent':
        if (!interviewDeferredOpenRef.current) {
          finishOpenDisplay();
        }
        return;
      case 'useLocal': {
        const localSheetId = resolution.localSheetId ?? sId;
        const localFromList = swimLaneRef.current.find((d) => diagramId(d) === localSheetId);
        if (localFromList) {
          void (async () => {
            const enriched = await fetchPainPointsEnrichIfNeeded(
              localSheetId,
              { ...localFromList } as Record<string, unknown>
            );
            if (latestSIdRef.current !== sId) return;
            invalidateParsedSheetCache(localSheetId);
            setSwimlane(enriched);
            swimLaneRef.current = swimLaneRef.current.map((item) =>
              diagramId(item) === localSheetId ? { ...item, ...enriched } : item
            );
            setAllSwimLane([...swimLaneRef.current]);
            const stillGenerating = (enriched as any)?.is_finish === false;
            if (stillGenerating) {
              console.warn('[mapGenPending] activé via useLocal pour', localSheetId);
            }
            setMapGenPending(stillGenerating);
            if (!stillGenerating) {
              setIsMapGenerationFailed(false);
            }
            finishOpenDisplay();
          })();
          return;
        }
        if (interviewDeferredOpenRef.current) {
          void loadNewSwimlane();
          return;
        }
        finishOpenDisplay();
        return;
      }
      case 'fetchRemote':
        void loadNewSwimlane();
        return;
      default:
        if (!interviewDeferredOpenRef.current) {
          finishOpenDisplay();
        }
    }
  }, [sId, users.access_token, loadNewSwimlane, fetchPainPointsEnrichIfNeeded, finishOpenDisplay]);

  React.useEffect(() => {
    if (!mapGenPending || !sId) return;
    const interval = window.setInterval(() => {
      void fetchMapPayload();
    }, 8000);
    return () => window.clearInterval(interval);
  }, [mapGenPending, sId, fetchMapPayload]);

  const loadApprovalList = async () => {
    try {
      const data = await FamlaServices("api_host", `/approval/`, "GET", "", users.access_token);
      if (data.status === 200) {
        setApprovallist(data.body.data);
      }
    } catch (error) {
      console.error("Error loading approval list:", error);
    }
  };

  const loadTime = async () => {
    try {
      const data = await FamlaServices("api_host", `/stats/process/${id}`, "GET", "", users.access_token);
      if (data.status === 200) {
        setTime(data.body.data);
      }
    } catch (error) {
      console.error("Error loading time:", error);
    }
  };

  const checkMapEditPermission = async (mapId: string) => {
    try {
      const response = await FamlaServices("api_host", `/approval/edit/${mapId}`, "GET", "", users.access_token);
      setCanEditMap(response.body.status_code === 200);
    } catch (error) {
      setCanEditMap(false);
    }
  };

  const loadCollaborators = async (mapId: string) => {
    try {
      const data = await FamlaServices("api_host", `/approval/${mapId}`, "GET", "", users.access_token);
      if (data.status === 200) {
        setcollaborators(data.body.data);
      }
    } catch {
      console.warn("Error loading collaborators (endpoint may not be implemented)");
      setcollaborators([]);
    }
  };

  const deferApprovalSideEffects = useCallback((targetMapId: string) => {
    const run = () => {
      void Promise.all([
        loadCollaborators(targetMapId),
        checkMapEditPermission(targetMapId),
      ]);
    };
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(run, { timeout: 2000 });
    } else {
      window.setTimeout(run, 0);
    }
  }, [users.access_token]);

  const finishCacheSwitch = useCallback(
    (
      targetId: string,
      diagramData: Record<string, unknown>,
      perfId: number,
      pathLabel: 'fast' | 'cache'
    ) => {
      setIsMapGenerationFailed(false);
      markMapSwitchPhase(pathLabel === 'fast' ? 'setMap_fast' : 'setMap_cache', perfId);
      applyLocalNavigation(targetId, { ...diagramData });
      deferApprovalSideEffects(targetId);
      scheduleBackgroundSheetRefresh(targetId, diagramData.updated_at as string | undefined);
      endMapSwitch(perfId, pathLabel, { mapId: targetId, path: pathLabel });
    },
    [applyLocalNavigation, deferApprovalSideEffects, scheduleBackgroundSheetRefresh]
  );

  const setMap = async (_id: string) => {
    const myCallCount = ++setMapCallCountRef.current;
    const perfId = beginMapSwitch({ mapId: _id, path: 'slow' });
    setMapSwitchTargetId(_id);
    setIsMapSwitching(true);
    scheduleMapSwitchingFallbackClear();
    try {
      const guards = switchGuardsRef.current;
      const cached = swimLaneRef.current.find((d: any) => diagramId(d) === _id);

      if (
        cached &&
        hasCompleteLocalSheet(swimLaneRef.current, _id) &&
        !guards.forceSlowPath
      ) {
        const pathLabel =
          MAP_SWITCH_FAST_PATH && canUseLocalSheetCache(_id, swimLaneRef.current, guards)
            ? 'fast'
            : 'cache';
        finishCacheSwitch(_id, cached, perfId, pathLabel);
        return;
      }

      if (shouldReloadProcessListOnSwitch(_id, swimLaneRef.current, guards.forceSlowPath)) {
        await loadAllSwimlane();
        if (setMapCallCountRef.current !== myCallCount) return;
      }

      let diagramData =
        swimLaneRef.current.find((d: any) => diagramId(d) === _id) || cached;

      if (!diagramData && shouldReloadProcessListOnSwitch(_id, swimLaneRef.current, guards.forceSlowPath)) {
        await loadAllSwimlane();
        if (setMapCallCountRef.current !== myCallCount) return;
        diagramData = swimLaneRef.current.find((d: any) => diagramId(d) === _id);
      }

      if (!diagramData) {
        console.warn("[setMap] Mapping absent de la liste après refresh:", _id);
        dispatch(mapId({ mapId: _id }));
        clearMapSwitchingOverlay();
        return;
      }

      let merged: any = { ...diagramData };
      if (!hasCompleteLocalSheet(swimLaneRef.current, _id)) {
        try {
          markMapSwitchPhase('calculate_new', perfId);
          let detail = await fetchSheetDetail(_id, 'sheet');
          if (detail.status !== 200) {
            detail = await fetchSheetDetail(_id, 'calculate');
          }
          if (setMapCallCountRef.current !== myCallCount) return;
          if (detail.status === 200 && detail.body?.data) {
            merged = mergeSheetDetail(merged, detail.body.data);
          }
        } catch {
          /* garde diagramData sans enrichissement */
        }
      }
      merged = await fetchPainPointsEnrichIfNeeded(_id, merged);
      if (setMapCallCountRef.current !== myCallCount) return;
      swimLaneRef.current = swimLaneRef.current.map((item) =>
        diagramId(item) === _id ? { ...item, ...merged } : item
      );
      setAllSwimLane([...swimLaneRef.current]);

      if (setMapCallCountRef.current !== myCallCount) return;
      applyLocalNavigation(_id, merged);
      setIsMapGenerationFailed(false);
      deferApprovalSideEffects(_id);
      endMapSwitch(perfId, 'slow', { mapId: _id, path: 'slow' });
    } catch (error) {
      console.error("Error in setMap:", error);
      clearMapSwitchingOverlay();
    }
  };

  const handleSelectionsChange = async (newSelections: LayerSelections) => {
    try {
      setLayer(newSelections);
      const selection = {
        line: newSelections.line,
        start_end: newSelections.startEnd,
        process: newSelections.block
      };

      const data = await FamlaServices(
        "api_host",
        `/process/update/layers/${id}`,
        "PUT",
        selection,
        users.access_token
      );

      if (data.status === 200) {
        window.location.reload()
      }
    } catch (error) {
      console.error("Error updating selections:", error);
    }
  };

  const interviewLoading = interviewDeferredOpen
    ? interviewReveal.interviewLoading
    : bootSpinner;
  const shouldMountEditor = interviewDeferredOpen
    ? interviewReveal.shouldMountEditor
    : !editorBlocked && !bootSpinner;

  const editorSwimlane = (
    <DefaultEditorSwimlane
      key={`${id ?? "process"}-${liveMapId}`}
      swimlaneExport={swimLane}
      id={id}
      allSwimLane={swimLaneRef.current}
      sId={liveMapId}
      t={t}
      approvallist={approvallist}
      loadAllSwimlane={loadAllSwimlane}
      canEditMap={canEditMap}
      isProcessMapManager={isProcessMapManager}
      setMap={setMap}
      layer={layer}
      handleSelectionsChange={handleSelectionsChange}
      collaborators={collaborators}
      setCollaborators={setcollaborators}
      processesWithDiagrams={processesWithDiagrams}
      AnalyseId={AnalyseId}
      onPermissionGranted={() => checkMapEditPermission(liveMapId)}
      switchGuardsRef={switchGuardsRef}
      isMapSwitching={isMapSwitching}
      mapSwitchTargetId={mapSwitchTargetId}
      mapGenPending={mapGenPending}
      onMapSwitchRenderComplete={clearMapSwitchingOverlay}
      onInitialRenderComplete={interviewReveal.onEditorInitialRender}
      onSheetCacheUpdate={(sheetId: string, patch: Record<string, unknown>) => {
        invalidateParsedSheetCache(sheetId);
        swimLaneRef.current = swimLaneRef.current.map((item: any) =>
          diagramId(item) === sheetId ? { ...item, ...patch } : item
        );
        setAllSwimLane([...swimLaneRef.current]);
      }}
    />
  );

  return (
    <>
      <div className={interviewDeferredOpen ? interviewReveal.pageClassName : (bootSpinner ? "bg-dark" : "bg-white")}>
        {interviewReveal.showLoadingOverlay && (
          <div
            key={sId}
            className="position-fixed top-0 start-0 w-100 vh-100"
            style={{ zIndex: interviewReveal.overlayZIndex }}
          >
            <SwimlaneError refresh={() => { }} error={false} t={t} time={time} />
          </div>
        )}
        {!interviewDeferredOpen && bootSpinner && (
          <SwimlaneError refresh={() => { }} error={false} t={t} time={time} />
        )}
        {!interviewLoading && editorBlocked && (
          mapGenPending ? (
            <MappingGenerationPlaceholder t={t} />
          ) : mapAccessBlocked ? (
            <SwimlaneError refresh={() => navigate(-1)} error={false} t={t} time={time} />
          ) : (
            <SwimlaneError refresh={() => navigate(-1)} error={true} t={t} />
          )
        )}
        {shouldMountEditor && (
          <div
            style={interviewDeferredOpen ? interviewReveal.editorShellStyle : undefined}
            aria-hidden={interviewLoading || undefined}
          >
            {editorSwimlane}
          </div>
        )}
      </div>
      <DiagramList
        closeRef={closeRef}
        allSwimLane={swimLaneRef.current}
        setMap={setMap}
        t={t}
      />
      <span ref={insufficientCreditsModalRef} data-bs-target={`#insufficientCreditsModal`} data-bs-toggle='modal'></span>
      {insufficientCreditsInfo && (
        <InsufficientCreditsModal
          id="insufficientCreditsModal"
          creditsRequired={insufficientCreditsInfo.creditsRequired}
          creditsAvailable={insufficientCreditsInfo.creditsAvailable}
          deficit={insufficientCreditsInfo.deficit}
          plan={insufficientCreditsInfo.plan}
          onClose={() => setInsufficientCreditsInfo(null)}
          isOwner={isOwner}
          adminEmail={ownerEmail}
        />
      )}
      <OutlookPromoModal trigger="first_mapping" enabled={interviewRevealComplete || !interviewDeferredOpen} />
    </>
  );
};

export default ProcessDiagram;